import { LoginOutlined, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getSessionClaims, startSupportSession } from '../apiClient.ts';
import { RecordSection } from '../components/record/RecordPage.tsx';
import TeamMembers from '../team/TeamMembers.tsx';
import { platformTeam, type TeamMember } from '../team/teamApi.ts';
import { formatReportDate, getErrorMessage } from '../utils/helperUtils.ts';
import {
  fetchTenant,
  requestSupportSession,
  stripeCustomerUrl,
  type TenantSummary
} from './platformApi.ts';
import TenantStatusChip from './TenantStatusChip.tsx';

/** One workspace in Platform: its people, and the way in to help. */
const PlatformTenantPage = () => {
  const tenantId = Number(useParams().id);
  const actions = useMemo(() => platformTeam(tenantId), [tenantId]);
  const [data, setData] = useState<{ tenant: TenantSummary; members: TeamMember[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await fetchTenant(tenantId));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const tenant = data?.tenant;
  // The API signs in as the first active admin, the one with the lowest id.
  const firstAdmin = data?.members
    .filter((member) => member.role === 'ADMIN' && member.status !== 'revoked')
    .sort((a, b) => a.id - b.id)[0];

  const openWorkspace = async () => {
    setOpening(true);
    try {
      startSupportSession(await requestSupportSession(tenantId));
      // A fresh page, so nothing from Platform stays cached in the workspace.
      window.location.hash = '#/';
      window.location.reload();
    } catch (err) {
      setError(getErrorMessage(err));
      setOpening(false);
      setConfirming(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/platform">
          Platform
        </MuiLink>
        <Typography color="text.primary">{tenant?.name ?? 'Workspace'}</Typography>
      </Breadcrumbs>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      {tenant && data ? (
        <>
          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: 'flex',
              gap: 2,
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' }
            }}
          >
            <Box>
              <Typography
                variant="overline"
                sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
              >
                Workspace {tenant.id}
              </Typography>
              <Typography
                variant="h4"
                sx={{ mt: 0.25, fontWeight: 750, letterSpacing: '-0.025em' }}
              >
                {tenant.name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
                <TenantStatusChip tenant={tenant} />
                <Typography variant="body2" color="text.secondary">
                  Started {formatReportDate(tenant.createdAt)}
                  {tenant.lastSeenAt ? ` · Last active ${formatReportDate(tenant.lastSeenAt)}` : ''}
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              {tenant.stripeCustomerId ? (
                <Button
                  variant="outlined"
                  endIcon={<OpenInNew />}
                  href={stripeCustomerUrl(tenant.stripeCustomerId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Stripe
                </Button>
              ) : null}
              {tenant.demo ? null : (
                <Button
                  variant="contained"
                  startIcon={<LoginOutlined />}
                  disabled={!firstAdmin}
                  onClick={() => setConfirming(true)}
                >
                  Open workspace
                </Button>
              )}
            </Stack>
          </Box>

          {!tenant.demo && !firstAdmin ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nobody here is an admin. Invite one or make someone an admin before opening the
              workspace.
            </Alert>
          ) : null}

          <RecordSection
            title="People"
            description="Invite, promote or revoke, with the same rules the workspace's admins have."
          >
            <TeamMembers
              members={data.members}
              actions={actions}
              selfEmail={getSessionClaims()?.email}
              onChange={() => void load()}
            />
          </RecordSection>
        </>
      ) : error ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading the workspace
          </Typography>
        </Box>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>Open {tenant?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are signed in as {firstAdmin?.email} for an hour, and everything you change is saved
            as them. Exit from the banner at the top to come back here.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)} disabled={opening}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void openWorkspace()} disabled={opening}>
            {opening ? 'Opening…' : 'Open workspace'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PlatformTenantPage;
